import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import {
  CURRENT_DISCLAIMER_VERSION,
  CURRENT_RISK_DISCLOSURE_VERSION,
  CURRENT_TERMS_VERSION,
} from "@/lib/compliance/constants";

const ExperienceEnum = z.enum(["never", "lt1", "1to3", "3plus"]);

/** SHA-256 of an IP plus a per-deployment pepper. Browser-safe (uses Web Crypto). */
async function hashIp(ip: string): Promise<string> {
  const pepper = process.env.SUPABASE_SERVICE_ROLE_KEY?.slice(0, 16) ?? "fxmind";
  const enc = new TextEncoder().encode(`${pepper}:${ip}`);
  const buf = await crypto.subtle.digest("SHA-256", enc);
  return Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function getClientIp(): string | null {
  try {
    const req = getRequest();
    return (
      req.headers.get("cf-connecting-ip") ??
      req.headers.get("x-forwarded-for")?.split(",")[0]?.trim() ??
      req.headers.get("x-real-ip") ??
      null
    );
  } catch {
    return null;
  }
}

function getDetectedCountry(): string | null {
  try {
    const req = getRequest();
    const v = (req.headers.get("cf-ipcountry") ?? "").toUpperCase();
    return /^[A-Z]{2}$/.test(v) ? v : null;
  } catch {
    return null;
  }
}

function getUserAgent(): string | null {
  try {
    const req = getRequest();
    return (req.headers.get("user-agent") ?? "").slice(0, 500) || null;
  } catch {
    return null;
  }
}

export const getSuitabilityStatus = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await supabaseAdmin
      .from("profiles")
      .select(
        "suitability_confirmed_at, suitability_experience, suitability_jurisdiction, suitability_us_acknowledged, risk_disclosure_version, disclaimer_version, terms_version, country_selected, country_detected_from_ip, jurisdiction_warning_shown, jurisdiction_acknowledged_at, created_at, onboarded_at",
      )
      .eq("id", context.userId)
      .maybeSingle();
    if (error) throw new Error(error.message);
    return {
      confirmed: !!data?.suitability_confirmed_at,
      experience: data?.suitability_experience ?? null,
      jurisdiction: data?.suitability_jurisdiction ?? null,
      usAcknowledged: !!data?.suitability_us_acknowledged,
      riskDisclosureVersion: data?.risk_disclosure_version ?? null,
      disclaimerVersion: data?.disclaimer_version ?? null,
      termsVersion: data?.terms_version ?? null,
      countrySelected: data?.country_selected ?? null,
      countryDetectedFromIp: data?.country_detected_from_ip ?? null,
      jurisdictionWarningShown: !!data?.jurisdiction_warning_shown,
      jurisdictionAcknowledgedAt: data?.jurisdiction_acknowledged_at ?? null,
      createdAt: data?.created_at ?? null,
      onboardedAt: data?.onboarded_at ?? null,
    };
  });

export const submitSuitability = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i) =>
    z
      .object({
        experience: ExperienceEnum,
        risksAcknowledged: z.literal(true),
        countrySelected: z
          .string()
          .trim()
          .regex(/^[A-Z]{2}$/)
          .nullable()
          .optional(),
        jurisdictionWarningShown: z.boolean().optional().default(false),
        jurisdictionAcknowledged: z.boolean().optional().default(false),
        usAcknowledged: z.boolean().optional().default(false),
        disclaimerAccepted: z.literal(true),
        termsAccepted: z.literal(true),
      })
      .parse(i),
  )
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const now = new Date().toISOString();

    const detectedIp = getClientIp();
    const detectedCountry = getDetectedCountry();
    const ua = getUserAgent();
    const ipHash = detectedIp ? await hashIp(detectedIp) : null;
    const finalCountry = data.countrySelected ?? null;

    const patch = {
      suitability_confirmed_at: now,
      suitability_experience: data.experience,
      suitability_jurisdiction: finalCountry,
      suitability_us_acknowledged:
        finalCountry === "US" ? !!data.usAcknowledged : false,
      risk_disclosure_version: CURRENT_RISK_DISCLOSURE_VERSION,
      disclaimer_version: CURRENT_DISCLAIMER_VERSION,
      terms_version: CURRENT_TERMS_VERSION,
      country_selected: finalCountry,
      country_detected_from_ip: detectedCountry,
      jurisdiction_warning_shown: !!data.jurisdictionWarningShown,
      jurisdiction_acknowledged_at: data.jurisdictionAcknowledged ? now : null,
      suitability_ip_hash: ipHash,
      suitability_user_agent: ua,
      terms_agreed_at: now,
    };

    const { error } = await supabaseAdmin
      .from("profiles")
      .update(patch)
      .eq("id", context.userId);
    if (error) throw new Error(error.message);

    try {
      await supabaseAdmin.from("audit_log").insert({
        actor_id: context.userId,
        action: "suitability.confirmed",
        target_type: "profile",
        target_id: context.userId,
        ip: ipHash,
        metadata: {
          experience: data.experience,
          country_selected: finalCountry,
          country_detected_from_ip: detectedCountry,
          jurisdiction_warning_shown: !!data.jurisdictionWarningShown,
          jurisdiction_acknowledged: !!data.jurisdictionAcknowledged,
          us_acknowledged: finalCountry === "US" ? !!data.usAcknowledged : false,
          risk_disclosure_version: CURRENT_RISK_DISCLOSURE_VERSION,
          disclaimer_version: CURRENT_DISCLAIMER_VERSION,
          terms_version: CURRENT_TERMS_VERSION,
          user_agent: ua,
          confirmed_at: now,
        } as never,
      });
    } catch {
      // best-effort
    }

    return { ok: true };
  });
